import type {Nullish} from 'shared/web';

export const HEX_COLOUR = /^#[0-9A-Fa-f]{6}$/;

/**
 * Picks the swatch colour for a paint / upholstery. A valid authored `#RRGGBB` hex wins; colour
 * codes are optional in the catalogue, so without one this falls back to a display-only heuristic:
 * known colour words win, otherwise a stable neutral shade is derived from the name.
 */
const COLOR_WORDS: [RegExp, string][] = [
  [/\b(white|frozen|pearl|ivory|arctic)\b/i, '#F4F4F2'],
  [/\b(black|ebony|agate|shadow|onyx)\b/i, '#1F2328'],
  [/\b(silver|solar)\b/i, '#B9BEC4'],
  [/\b(grey|gray|magnetic|graphite|carbon|steel)\b/i, '#585E66'],
  [/\b(red|lucid|ruby|racing)\b/i, '#B71C1C'],
  [/\b(blue|island|chrome|azure|navy|desert)\b/i, '#3E5C7A'],
  [/\b(green|forest|racing green)\b/i, '#2E5B3B'],
  [/\b(brown|roast|mojave|dune|tan|sand|beige|cognac)\b/i, '#7A5A44'],
  [/\b(orange|copper|bronze)\b/i, '#C8672A'],
  [/\b(yellow|gold)\b/i, '#D5A423'],
];

const NEUTRAL_FALLBACK = ['#8B93A1', '#6B7280', '#9CA3AF', '#4B5563'];

export const getSwatchColor = (name: string, hex?: string | Nullish): string => {
  if (hex && HEX_COLOUR.test(hex)) {
    return hex;
  }
  const match = COLOR_WORDS.find(([pattern]) => pattern.test(name));
  if (match) {
    return match[1];
  }
  const hash = [...name].reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) % 997, 7);
  return NEUTRAL_FALLBACK[hash % NEUTRAL_FALLBACK.length] ?? NEUTRAL_FALLBACK[0];
};
